/*
 * CPFS recipe article behaviours:
 *   1. <recipe-scaler> — interactive servings stepper; rescales every ingredient
 *      quantity (the ingredients table + the cost calculator's "Used" column).
 *   2. <recipe-cost-calculator> — live, client-side batch/per-serving cost.
 *   3. [data-recipe-print] — "Download recipe" -> browser print-to-PDF.
 * Vanilla custom elements, no dependencies. Ported from .reference/js/recipe.js.
 */
(function () {
  const peso = (n) => '₱' + n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Format a scaled quantity: up to 2 decimals, trailing zeros stripped
  // (129 -> "129", 18.75 -> "18.75", 2128.5 -> "2128.5").
  const fmtQty = (n) => {
    if (!isFinite(n)) return '';
    const r = Math.round(n * 100) / 100;
    return Math.round(r) === r ? String(Math.round(r)) : String(r);
  };

  class RecipeCostCalculator extends HTMLElement {
    connectedCallback() {
      this.servings = parseFloat(this.dataset.servings) || 1;
      this.rows = Array.from(this.querySelectorAll('tbody tr[data-qty]'));
      this.totalCell = this.querySelector('[data-total]');
      this.perServingCell = this.querySelector('[data-per-serving]');
      if (!this.rows.length || !this.totalCell) return;

      this._onInput = () => this.recompute();
      this.addEventListener('input', this._onInput);
      this.recompute();
    }

    disconnectedCallback() {
      if (this._onInput) this.removeEventListener('input', this._onInput);
    }

    recompute() {
      let total = 0;
      for (const row of this.rows) {
        const qty = parseFloat(row.dataset.qty);
        const packInput = row.querySelector('[data-pack]');
        const priceInput = row.querySelector('[data-price]');
        const costCell = row.querySelector('[data-cost]');
        if (!packInput || !priceInput || !costCell) continue;
        const pack = parseFloat(packInput.value);
        const price = parseFloat(priceInput.value);
        let cost = 0;
        if (qty > 0 && pack > 0 && price > 0) {
          cost = (price / pack) * qty;
          total += cost;
        }
        costCell.textContent = cost > 0 ? peso(cost) : '';
        row.classList.toggle('cost-calc__row--filled', cost > 0);
      }
      this.totalCell.textContent = peso(total);
      if (this.perServingCell) {
        this.perServingCell.textContent = peso(this.servings > 0 ? total / this.servings : 0);
      }
    }
  }

  if (!customElements.get('recipe-cost-calculator')) {
    customElements.define('recipe-cost-calculator', RecipeCostCalculator);
  }

  // Interactive servings scaler. Steps servings up/down and rescales every ingredient
  // quantity by (current servings / base servings): the ingredients table cells
  // ([data-base-qty]) and, kept in lock-step, the cost calculator's "Used" column and
  // its per-serving maths. Pure proportional client-side maths — needs no client data,
  // so it works for every recipe that has a base servings value.
  const MIN_SERVINGS = 1;
  const MAX_SERVINGS = 999;

  class RecipeScaler extends HTMLElement {
    connectedCallback() {
      this.base = parseFloat(this.dataset.baseServings);
      if (!(this.base > 0)) return; // no base yield -> nothing to scale from
      this.current = this.base;

      this.valueEl = this.querySelector('[data-scale-value]');
      this.decBtn = this.querySelector('[data-scale-dec]');
      this.incBtn = this.querySelector('[data-scale-inc]');

      // One recipe per page, so scope the scalable targets to the document.
      this.ingCells = Array.from(document.querySelectorAll('.v3-ing-table [data-base-qty]'));
      this.calc = document.querySelector('recipe-cost-calculator');
      this.calcRows = this.calc ? Array.from(this.calc.querySelectorAll('tr[data-base-qty]')) : [];
      this.servingCountEls = Array.from(document.querySelectorAll('[data-serving-count]'));

      this._onDec = () => this.step(-1);
      this._onInc = () => this.step(1);
      if (this.decBtn) this.decBtn.addEventListener('click', this._onDec);
      if (this.incBtn) this.incBtn.addEventListener('click', this._onInc);

      this.updateButtons(); // set the disabled state at the base value
    }

    disconnectedCallback() {
      if (this.decBtn && this._onDec) this.decBtn.removeEventListener('click', this._onDec);
      if (this.incBtn && this._onInc) this.incBtn.removeEventListener('click', this._onInc);
    }

    step(delta) {
      const next = Math.min(MAX_SERVINGS, Math.max(MIN_SERVINGS, this.current + delta));
      if (next === this.current) return;
      this.current = next;
      this.apply();
    }

    apply() {
      const factor = this.current / this.base;

      if (this.valueEl) this.valueEl.textContent = String(this.current);

      // Ingredients table quantities.
      for (const cell of this.ingCells) {
        const b = parseFloat(cell.dataset.baseQty);
        if (isFinite(b)) cell.textContent = fmtQty(b * factor);
      }

      // Cost calculator "Used" column + the qty its cost maths reads.
      for (const row of this.calcRows) {
        const b = parseFloat(row.dataset.baseQty);
        if (!isFinite(b)) continue;
        const scaled = b * factor;
        row.dataset.qty = String(scaled);
        const usedCell = row.querySelector('[data-used]');
        if (usedCell) {
          const unit = row.dataset.unit || '';
          usedCell.textContent = fmtQty(scaled) + (unit ? ' ' + unit : '');
        }
      }

      for (const el of this.servingCountEls) el.textContent = String(this.current);

      // Recost with the scaled quantities + new serving count.
      if (this.calc && typeof this.calc.recompute === 'function') {
        this.calc.servings = this.current;
        this.calc.recompute();
      }

      this.updateButtons();
    }

    updateButtons() {
      if (this.decBtn) {
        const atMin = this.current <= MIN_SERVINGS;
        this.decBtn.disabled = atMin;
        this.decBtn.setAttribute('aria-disabled', String(atMin));
      }
      if (this.incBtn) {
        const atMax = this.current >= MAX_SERVINGS;
        this.incBtn.disabled = atMax;
        this.incBtn.setAttribute('aria-disabled', String(atMax));
      }
    }
  }

  if (!customElements.get('recipe-scaler')) {
    customElements.define('recipe-scaler', RecipeScaler);
  }

  // "Download recipe" -> print-to-PDF (the print stylesheet trims the page to the
  // recipe). Real PDF link is used instead when custom.download_url is set (Liquid).
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-recipe-print]');
    if (!trigger) return;
    e.preventDefault();
    window.print();
  });
})();
